import { DownOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import {
  defaultCisStatus,
  defaultPaymentFrequency,
  paymentModels,
  PAYMENT_MODEL_ENUM,
  pensionDeductionMethods,
} from '@nxsys/constants/AppConstants';
import { marginFigures } from '../listing/margin-figures';
import { hasSafeRecSetting } from '../listing/columns';
import {
  useGetPayrollById,
  useSafeRecMultiplePayroll,
  useSendPayslip,
  useViewPayslipById,
} from '@nxsys/hooks/api/payroll';
import { usePerformReversalPayrolls } from '@nxsys/hooks/api/timesheets';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { PerformReversalPayrollsParams } from '@nxsys/types/api';
import { formatFullName, getAppConfig, showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoneyExport } from '@nxsys/utils/number-helper';
import {
  getShownEmployeePension,
  getShownEmployersPension,
} from '@nxsys/utils/reported-pension';
import { Descriptions, Dropdown, Typography } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import { find, isEmpty, some } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import TimesheetReverseModal from '../../../timesheets/components/TimesheetReverseModal';
import { useCanReversePayroll } from '@nxsys/hooks/useCanReversePayroll';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppDescription from '@nxsys/components/AppDescription';

const ViewPayrollDetail: React.FC = () => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const payrollId = params.payrollId as string;

  const { payrollCountry, isBureau } = useSelector((state: AppState) => state.appStore);
  const canReverse = useCanReversePayroll();
  const defaultPaymentModel = getAppConfig('paymentModels');
  const { goBack } = useGoBackByQuery();
  const navigate = useNavigateWithQuery();

  const {
    value: payrollValue,
    run: getPayrollById,
    loading: isGetPayrollByIdLoading,
  } = useGetPayrollById();
  const payrollData = useMemo(() => payrollValue?.data, [payrollValue]);

  useEffect(() => {
    if (payrollId) {
      getPayrollById(payrollId);
    }
  }, [payrollId]);

  const handleGoBack = useCallback(
    () =>
      goBack({
        fromPathQueryKey: 'from',
        fallbackPathDefault: '/reports',
      }),
    [goBack],
  );

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.paymentSummaryDetail']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        ...(payrollData?.selfBillRef
          ? [
              ...(!isBureau && hasSafeRecSetting(payrollData)
                ? [
                    {
                      key: 'safeRec',
                      label: messages['common.sendToSafeRec'],
                      onClick: handleSendToSafeRec,
                    },
                    // Same as the listing: only once a send has been attempted.
                    ...(payrollData?.isSendSafeRecSuccess ||
                    payrollData?.safeRecErrorMsg
                      ? [
                          {
                            key: 'safeRecSubmission',
                            label:
                              messages['common.viewSafeRecSubmission'] ||
                              'View SafeRec Submission',
                            onClick: handleViewSafeRecSubmission,
                          },
                        ]
                      : []),
                  ]
                : []),
              {
                key: 'sendEmailPayslip',
                label: messages['common.sendEmailPayslip'],
                onClick: handleSendPayslip,
              },
              {
                key: 'download',
                label: messages['common.download'],
                onClick: handleViewPayslip,
              },
            ]
          : []),
        ...(canReverse
          ? [
              {
                key: 'reverse',
                label: messages['timesheet.reverse'],
                onClick: () => setOpenReverseModal(true),
              },
            ]
          : []),
        {
          key: 'export',
          label: messages['common.export'],
          onClick: handleExport,
        },
      ],
    };
    return (
      <Dropdown menu={menuProps}>
        <AppButton
          type="primary"
          name={messages['common.action']}
          rightIcon={<DownOutlined />}
        />
      </Dropdown>
    );
  };

  const handleExport = () => {
    if (!payrollData) return;
    const dataExport = [payrollData].map((item) => {
      const {
        companyRef,
        subcontractorRef,
        payeeUmbrellaValue,
        agencyName,
        taxPeriod,
        taxYear,
        payFrequency,
        timesheetValue,
        _otherPayment,
        _otherDeduction,
        payments,
        totalPayment,
        totalDeduction,
        rolledUpHolidayPay,
        retainedHolidayPay,
        holidayPaid,
        paymentDate,
        net,
        salary,
        expenseClaimed,
        paid,
        timeSheetRef,
        invoiceNo,
        invoiceNos,
        outstanding,
        studentLoan,
        postgraduateLoan,
        selfBillRef,
        displayedTaxablePay,
      } = item;
      return {
        companyID: companyRef?.id || '',
        subcontractorID: subcontractorRef?.id || '',
        agencyRef: `${subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
          subcontractorRef?.agencyRef || ''
        }`,
        name: formatFullName(subcontractorRef),
        taxPeriod: taxPeriod || '',
        taxYear: taxYear || selfBillRef?.taxYear || '',
        paymentModel:
          paymentModels[
            subcontractorRef?.paymentModel as keyof typeof paymentModels
          ] || '',
        payFrequency: payFrequency
          ? find(
              defaultPaymentFrequency,
              (item) => item?.value === payFrequency,
            )?.label
          : '',
        paymentCompany: companyRef?.companyName || '',
        agencyName: agencyName || '',
        timesheetValue: formatMoneyExport(
          Number(timesheetValue),
          showCurrencyByCountry(payrollCountry),
        ),
        troncValue: formatMoneyExport(
          Number(item?.payeeUmbrellaValue?.troncValue),
          showCurrencyByCountry(payrollCountry),
        ),
        hours: payeeUmbrellaValue?.totalHours || 0,
        // NX-5555: same trio as the report listings. Exporting only
        // `payslipMargin` under "Margin" meant this file disagreed with the
        // listings, where "Margin" is now the pre-adjustment figure.
        margin: formatMoneyExport(
          marginFigures(item as never).gross,
          showCurrencyByCountry(payrollCountry),
        ),
        marginAdjustment: formatMoneyExport(
          marginFigures(item as never).adjustment,
          showCurrencyByCountry(payrollCountry),
        ),
        netMargin: formatMoneyExport(
          marginFigures(item as never).net,
          showCurrencyByCountry(payrollCountry),
        ),
        apprenticeLevy: formatMoneyExport(
          Number(payeeUmbrellaValue?.appLevy),
          showCurrencyByCountry(payrollCountry),
        ),
        salary: formatMoneyExport(
          Number(salary),
          showCurrencyByCountry(payrollCountry),
        ),
        rolledUpHolidayPay: formatMoneyExport(
          Number(rolledUpHolidayPay),
          showCurrencyByCountry(payrollCountry),
        ),
        expenseClaimed: formatMoneyExport(
          Number(expenseClaimed),
          showCurrencyByCountry(payrollCountry),
        ),
        expensesPaid: formatMoneyExport(
          Number(payeeUmbrellaValue?.expensesPaid),
          showCurrencyByCountry(payrollCountry),
        ),
        bonus:
          subcontractorRef?.paymentModel !== 'PAYEE'
            ? formatMoneyExport(
                Number(payeeUmbrellaValue?.bonus),
                showCurrencyByCountry(payrollCountry),
              )
            : '',
        taxablePay: formatMoneyExport(
          Number(displayedTaxablePay),
          showCurrencyByCountry(payrollCountry),
        ),
        grossForTax: formatMoneyExport(
          Number(payeeUmbrellaValue?.grossForTax),
          showCurrencyByCountry(payrollCountry),
        ),
        grossForNI: formatMoneyExport(
          Number(payeeUmbrellaValue?.grossForNI),
          showCurrencyByCountry(payrollCountry),
        ),
        otherPayment: formatMoneyExport(
          Number(_otherPayment),
          showCurrencyByCountry(payrollCountry),
        ),
        otherDeduction: formatMoneyExport(
          Math.abs(Number(_otherDeduction) || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        description: !isEmpty(payments) ? payments[0].description || '' : '',
        totalPayment: formatMoneyExport(totalPayment),
        taxDeducted: formatMoneyExport(
          Number(payeeUmbrellaValue?.tax),
          showCurrencyByCountry(payrollCountry),
        ),
        employeeNI: formatMoneyExport(
          Number(payeeUmbrellaValue?.employeeNI),
          showCurrencyByCountry(payrollCountry),
        ),
        employersNI: formatMoneyExport(
          Number(payeeUmbrellaValue?.employersNI),
          showCurrencyByCountry(payrollCountry),
        ),
        employeePension: formatMoneyExport(
          getShownEmployeePension(item),
          showCurrencyByCountry(payrollCountry),
        ),
        eePensionDeductionMethod: payeeUmbrellaValue?.eePensionDeductionMethod
          ? pensionDeductionMethods.find(
              (item) =>
                item.value === payeeUmbrellaValue?.eePensionDeductionMethod,
            )?.label
          : 'Percentage (%)',
        employersPension: formatMoneyExport(
          getShownEmployersPension(item),
          showCurrencyByCountry(payrollCountry),
        ),
        erPensionDeductionMethod: payeeUmbrellaValue?.erPensionDeductionMethod
          ? pensionDeductionMethods.find(
              (item) =>
                item.value === payeeUmbrellaValue?.erPensionDeductionMethod,
            )?.label
          : 'Percentage (%)',
        holidayPayRetained: formatMoneyExport(
          retainedHolidayPay,
          showCurrencyByCountry(payrollCountry),
        ),
        totalDeductions: formatMoneyExport(
          totalDeduction,
          showCurrencyByCountry(payrollCountry),
        ),
        netPay: formatMoneyExport(
          Number(net),
          showCurrencyByCountry(payrollCountry),
        ),
        studentLoan: formatMoneyExport(
          Number(studentLoan),
          showCurrencyByCountry(payrollCountry),
        ),
        postgraduateLoan: formatMoneyExport(
          Number(postgraduateLoan),
          showCurrencyByCountry(payrollCountry),
        ),
        attachment: formatMoneyExport(
          payeeUmbrellaValue?.attachmentOrderDeducted,
          showCurrencyByCountry(payrollCountry),
        ),
        slickPay: formatMoneyExport(
          Number(payeeUmbrellaValue?.ssp),
          showCurrencyByCountry(payrollCountry),
        ),
        smp: formatMoneyExport(
          Number(payeeUmbrellaValue?.smp),
          showCurrencyByCountry(payrollCountry),
        ),
        sncp: formatMoneyExport(
          Number(payeeUmbrellaValue?.sncp),
          showCurrencyByCountry(payrollCountry),
        ),
        adoptionPay: formatMoneyExport(
          Number(payeeUmbrellaValue?.sap),
          showCurrencyByCountry(payrollCountry),
        ),
        paternityPay: formatMoneyExport(
          payeeUmbrellaValue?.spp || payeeUmbrellaValue?.sppa,
          showCurrencyByCountry(payrollCountry),
        ),
        additionalSPP: formatMoneyExport(
          0,
          showCurrencyByCountry(payrollCountry),
        ),
        grossForTaxYTD: formatMoneyExport(
          (item?.payeeUmbrellaValue?.grossForTaxTD || 0) +
            (item?.payeeUmbrellaValue?.p45GrossToDate || 0) +
            (item?.payeeUmbrellaValue?.p11?.grossForTax || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        taxYTD: formatMoneyExport(
          (item?.payeeUmbrellaValue?.taxTD || 0) +
            (item?.payeeUmbrellaValue?.p45TaxToDate || 0) +
            (item?.payeeUmbrellaValue?.p11?.tax || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        paymentDate: paymentDate
          ? moment(paymentDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        selfBillID: selfBillRef?.id?.replace('SFB', 'PAY') || '',
        invoiceNo: timeSheetRef
          ? invoiceNo || ''
          : !invoiceNos?.length
            ? ''
            : invoiceNos.length === 1
              ? invoiceNos[0]
              : 'Multi',
        paid: formatMoneyExport(paid, showCurrencyByCountry(payrollCountry)),
        outstanding: formatMoneyExport(
          outstanding,
          showCurrencyByCountry(payrollCountry),
        ),
        fpsAmount: '',
        holidayPaid: formatMoneyExport(
          (holidayPaid || 0) - (rolledUpHolidayPay || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        ...(some(defaultPaymentModel, (p) => p.value === 'PAYE_FTH')
          ? {
              takeHomePercent: formatMoneyExport(
                payeeUmbrellaValue?.takeHomePayPercent || 0,
                showCurrencyByCountry(payrollCountry),
              ),
              selfEmployedIncome: formatMoneyExport(
                payeeUmbrellaValue?.selfEmployedIncome || 0,
                showCurrencyByCountry(payrollCountry),
              ),
              additionalMargin: formatMoneyExport(
                payeeUmbrellaValue?.additionalMargin || 0,
                showCurrencyByCountry(payrollCountry),
              ),
            }
          : {}),
        avcERPension: formatMoneyExport(
          payeeUmbrellaValue?.avcERPension || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      };
    });
    const headers = [
      'Company ID',
      'Employee ID',
      'Agency Ref',
      'Name',
      'Tax Period',
      'Tax Year',
      'Payment Model',
      'Pay Frequency',
      'Payment Company',
      'Agency Name',
      'Timesheet Values',
      'Tronc payment',
      'Hours',
      'Margin',
      'Margin Adjustment',
      'Net Margin',
      'Apprentice Levy',
      'Salary',
      'Advanced Holiday Pay',
      'Expenses Claimed',
      'Expenses Paid',
      'Bonus',
      'Taxable Pay',
      'Gross for Tax',
      'Gross for NI',
      'Other Payment',
      'Other Deduction',
      'Description',
      'Total Payment',
      'Tax Deducted',
      'NI Empe',
      'NI Empr',
      'Pension Empe',
      'Empe Deduction Method',
      'Pension Empr',
      'Empr Deduction Method',
      'Holiday Pay Retained',
      'Total Deductions',
      'Net Pay',
      'Student Loan',
      'PG Student Loan',
      'Attachment',
      'Sick Pay (SSP)',
      'Maternity Pay (SMP)',
      'Neonatal Care Pay (SNCP)',
      'Adoption Pay (SAP)',
      'Paternity Pay (SPP)',
      'Additional SPP',
      'Gross for Tax (YTD)',
      'Tax (YTD)',
      'Payment Date',
      'Payslip ID',
      'Sales Inv Ref',
      'Paid',
      'Outstanding',
      'FPS Amount',
      'Holiday Paid',
      ...(some(defaultPaymentModel, (p) => p.value === 'PAYE_FTH')
        ? ['Take Home %', 'Umbrella Adv/Self Employed Pmt', 'Additional Margin']
        : []),
      'Employer AVC',
    ];
    const csvExporter = new ExportToCsv({
      filename:
        `Payroll-export-${moment(new Date())
          .tz('Europe/London')
          .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const { run: viewPayslipById, loading: isViewPayslipByIdLoading } =
    useViewPayslipById();
  const handleViewPayslip = () => {
    viewPayslipById(payrollData?.selfBillRef?.id || '').then((res) => {
      if (res.success) {
        window.open(res.data, '_blank');
      }
    });
  };

  const { run: sendPayslip, loading: isSendPayslipLoading } = useSendPayslip();
  const handleSendPayslip = () => {
    const params = {
      id: payrollData?.selfBillRef?.id || '',
      companyId: payrollData?.subcontractorRef?.companyPayment?.id || '',
      subcontractorId: payrollData?.subcontractorRef?.id || '',
    };
    sendPayslip(params).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.emailSentPayslipSuccess'],
        });
      }
    });
  };

  const {
    run: safeRecMultiplePayroll,
    loading: isSafeRecMultiplePayrollLoading,
  } = useSafeRecMultiplePayroll();
  const handleViewSafeRecSubmission = () => {
    navigate(`/reports/payroll/safe-rec-submission/${payrollId}`, {
      query: { from: `/reports/payroll/${payrollId}` },
    });
  };
  const handleSendToSafeRec = () => {
    safeRecMultiplePayroll([payrollId])
      .then((res) => {
        if (res.success) {
          showMessage({
            type: 'success',
            content: messages['common.safeRecPayrollSuccess'],
          });
        }
      })
      // Refresh the SafeRec status so "View SafeRec Submission" shows up.
      .finally(() => payrollId && getPayrollById(payrollId));
  };

  const {
    run: performReversalPayrolls,
    loading: isPerformReversalPayrollsPending,
  } = usePerformReversalPayrolls();
  const handleReverse = async (params: PerformReversalPayrollsParams) => {
    await performReversalPayrolls({
      ...params,
      isReportCIS:
        payrollData?.subcontractorRef?.paymentModel ===
        PAYMENT_MODEL_ENUM.CIS,
    }).then(() => {
      showMessage({
        type: 'info',
        content: messages['timesheet.reverseSuccessPending'],
      });
    });
  };
  const [openReverseModal, setOpenReverseModal] = useState<boolean>(false);

  const detailItems = [
    {
      label: messages['common.selfBillRef'],
      value: payrollData?.selfBillRef?.id || '',
    },
    {
      label: messages['common.timesheetId'],
      value: payrollData?.timeSheetRef?.id || '',
    },
    {
      label: messages['common.taxPeriod'],
      value: payrollData?.taxPeriod || '',
    },
    {
      label: messages['common.subcontractorId'],
      value: payrollData?.subcontractorRef?.id || '',
    },
    {
      label: messages['common.agencyName'],
      value: payrollData?.agencyName || '',
    },
    {
      label: messages['common.paymentCompany'],
      value: payrollData?.subcontractorRef?.companyPayment?.companyName || '',
    },
    {
      label: messages['common.subcontractorName'],
      value: payrollData?.subcontractorRef?.fullName || '',
    },
    {
      label: messages['common.timesheetValue'],
      value: formatMoneyExport(
        Number(payrollData?.timesheetValue),
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.margin'],
      value: formatMoneyExport(
        marginFigures(payrollData as never).gross,
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.marginAdjustment'],
      value: formatMoneyExport(
        marginFigures(payrollData as never).adjustment,
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.netMargin'],
      value: formatMoneyExport(
        marginFigures(payrollData as never).net,
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.gross'],
      value: formatMoneyExport(
        Number(payrollData?.gross),
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.materials'],
      value: formatMoneyExport(
        Number(payrollData?.materials),
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.vat'],
      value: formatMoneyExport(
        Number(payrollData?.vat),
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.net'],
      value: formatMoneyExport(
        Number(payrollData?.net),
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label:
        payrollCountry === IRELAND
          ? messages['common.rctStatus']
          : messages['common.cisStatus'],
      value:
        payrollCountry === IRELAND
          ? (payrollData?.cisStatus ?? '')
          : defaultCisStatus.find(
              (item) => item.value2 === payrollData?.cisStatus,
            )?.label || '',
    },
    {
      label:
        payrollCountry === IRELAND
          ? messages['common.rctDeduction']
          : messages['common.cisDeduction'],
      value: formatMoneyExport(
        Number(payrollData?.cisDeduction),
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.otherDeduction'],
      value: formatMoneyExport(
        Number(payrollData?.otherDeduction),
        showCurrencyByCountry(payrollCountry),
      ),
    },
    {
      label: messages['common.paymentDate'],
      value: payrollData?.paymentDate
        ? moment(payrollData?.paymentDate)
            .tz('Europe/London')
            .format('DD/MM/YYYY')
        : '',
    },
    {
      label: messages['common.selfInvRef'],
      value: payrollData?.invoiceNo || '',
    },
  ];

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isGetPayrollByIdLoading ||
        isViewPayslipByIdLoading ||
        isSendPayslipLoading ||
        isSafeRecMultiplePayrollLoading ||
        isPerformReversalPayrollsPending
      }
    >
      <AppCard title={messages['common.detailInformation']}>
        <AppsContent>
          <div className="p-4">
            <AppDescription
              column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
              labelStyle={{ width: 200 }}
              bordered={false}
              colon={false}
              size="middle"
            >
              {detailItems.map((it, idx) => (
                <Descriptions.Item key={idx} label={it.label}>
                  <Typography.Text>{it.value}</Typography.Text>
                </Descriptions.Item>
              ))}
            </AppDescription>
          </div>
        </AppsContent>
      </AppCard>
      <TimesheetReverseModal
        isOpen={openReverseModal}
        toggle={() => setOpenReverseModal(false)}
        payrollIds={[payrollId]}
        onPerform={handleReverse}
      />
    </AppsContainer>
  );
};

export default ViewPayrollDetail;
